import dayjs, { Dayjs } from "dayjs";
import { PalletRouteRow } from "../../types/pallet-summary.type";
import { formatNumber, numberWithCommas } from "../../utils";
import { formatDateTimeBkk } from "../../utils/date";
import {
  API_DATE_FORMAT,
  EMPTY_VALUE,
  MAX_RANGE_DAYS,
  UNEXPLAINED_TEXT,
} from "./constants";

const DISPLAY_DATE_FORMAT = "DD/MM/YYYY";
const NOTE_DAY_FORMAT = "DD/MM";
const TIME_FORMAT = "HH:mm";
const DATE_TIME_FORMAT = "DD/MM/YYYY HH:mm";
const NAME_SEPARATOR = ", ";
const NOTE_SEPARATOR = "\n";

/** Both ends count: 1–31 October is 31 days. */
export const isRangeTooLong = (start: Dayjs, end: Dayjs): boolean =>
  end.diff(start, "day") + 1 > MAX_RANGE_DAYS;

export const formatPalletCount = (count: number | null): string =>
  formatNumber(count, { decimals: 0, fallback: EMPTY_VALUE });

export const formatNames = (names: string[]): string =>
  names.length > 0 ? names.join(NAME_SEPARATOR) : EMPTY_VALUE;

export const branchLabel = (branch: {
  branch_code: string;
  branch_name: string;
}): string => `${branch.branch_code} ${branch.branch_name}`;

/** How a route is named in the table, the route picker and the breadcrumb alike. */
export const routeLabel = (route: {
  route_code: string;
  route_name: string;
}): string => `${route.route_code} ${route.route_name}`;

/** A bare calendar date, read as-is: passing it through a time zone could move it a day. */
export const formatBusinessDate = (businessDate: string): string =>
  dayjs(businessDate).format(DISPLAY_DATE_FORMAT);

/** Branch times are read on the Bangkok clock wherever the viewer is. */
export const formatDepartureTime = (checkedInAt: string): string =>
  formatDateTimeBkk(checkedInAt, TIME_FORMAT);

/**
 * A van back after midnight returned on a later day than the trip's date, so the date is spelled
 * out then rather than leaving "00:30" to read as that same morning.
 */
export const formatReturnTime = (
  arrivedAt: string | null,
  businessDate: string,
): string => {
  if (!arrivedAt) return EMPTY_VALUE;
  const isSameDay = formatDateTimeBkk(arrivedAt, API_DATE_FORMAT) === businessDate;
  return formatDateTimeBkk(arrivedAt, isSameDay ? TIME_FORMAT : DATE_TIME_FORMAT);
};

/**
 * A route's หมายเหตุ, one line per noted trip. A route that ran more than one trip in the range
 * starts each line with its day, so a note reads against the trip it explains.
 */
export const formatRouteNotes = (
  route: Pick<PalletRouteRow, "notes" | "trip_count">,
): string => {
  const hasSeveralTrips = route.trip_count > 1;
  return route.notes
    .map(({ business_date, note }) =>
      hasSeveralTrips ? `${dayjs(business_date).format(NOTE_DAY_FORMAT)} ${note}` : note,
    )
    .join(NOTE_SEPARATOR);
};

/** ยังไม่ระบุเหตุผล for a route, with the number of trips once more than one is waiting. */
export const formatUnexplainedTrips = (count: number): string | undefined => {
  if (count === 0) return undefined;
  if (count === 1) return UNEXPLAINED_TEXT;
  return `${UNEXPLAINED_TEXT} ${numberWithCommas(count)} เที่ยว`;
};
